<script setup lang="ts">
import { computed, inject, onMounted, onUnmounted, provide, ref } from "vue";
import { useRoute } from "vue-router";
import { storeToRefs } from "pinia";
import { DateTime } from "luxon";

import { searchContextSymbol } from "@/constants";

import ContentSearch from "@/components/content/ContentSearch.vue";
import { useAppBreadcrumbs } from "@/composables/useAppBreadcrumbs";

import type { WebinarFilters } from "@/composables/filters/webinar-filters";
import { webinarFiltersSymbol } from "@/plugins/ContentFiltersPlugin";

import type { ProductFilter } from "@/composables/filters/product-filter";
import type { CategoryFilter } from "@/composables/filters/category-filter";
import type { PagingFilter } from "@/composables/filters/paging-filter";
import { categoryFilterSymbol, pagingFilterSymbol, productFilterSymbol } from "@/plugins/ContentFiltersPlugin";
import { useApplicationStore } from "@/stores/Application";
import { useWebinarsStore } from "@/stores/Webinars";
import { useDateRangeFilter } from "@/composables/filters/date-range-filter";
import { usePagingFilter } from "@/composables/filters/paging-filter";
import { webinarKey } from "@/types/products/product-definitions";
import { normalizeDateTime } from "@tmi/utils/date-time";
import type { Webinar } from "@tmi/types/entities";

const { filteredWebinars } = inject< WebinarFilters >( webinarFiltersSymbol ) as WebinarFilters;

const webinarsStore = useWebinarsStore();
const { webinarBySlug } = webinarsStore;
const { webinarCategories, isLoading } = storeToRefs( webinarsStore );

const { setSelectedWebinar } = useApplicationStore();

const { trail, labelService } = useAppBreadcrumbs();
const breadcrumbItems = computed( () => trail( { label: labelService.label( 'lblWebinars' ) } ) );

// ---------------------------------------------------------------------------
// View toggle: prioritize past/on-demand webinars over upcoming registrations
// per WMD-1220. Default landing tab is "Past" since most visitors come to
// watch recordings, not to register for future sessions.
// ---------------------------------------------------------------------------
type WebinarView = 'past' | 'upcoming';

// Honor incoming alias paths (/learning/webinars/upcoming, /completed, etc.)
// which the router rewrites to ?view=upcoming|past on this route.
const route = useRoute();
const initialView: WebinarView = route.query.view === 'upcoming' ? 'upcoming' : 'past';
const view = ref< WebinarView >( initialView );

// Treat missing or unparseable webinarDate as past — `normalizeDateTime`
// returns null on un-parseable strings and the caller's `!` masks it, which
// would crash `.startOf()`. Past is the safer default since legacy content
// has typically already aired.
const isUpcoming = ( w: Webinar ): boolean => {
  if ( !w.webinarDate ) return false;
  const dt = normalizeDateTime( w.webinarDate );
  if ( !dt || !dt.isValid ) return false;
  return dt.startOf( 'day' ) >= DateTime.local().startOf( 'day' );
};

// Past: most recent first (descending by date).
const pastWebinars = computed< Webinar[] >( () =>
  filteredWebinars.value
    .filter( ( w ) => !isUpcoming( w ) )
    .sort( ( a, b ) =>
      normalizeDateTime( b.webinarDate ).toMillis() -
      normalizeDateTime( a.webinarDate ).toMillis()
    )
);

// Upcoming: soonest first (ascending by date).
const upcomingWebinars = computed< Webinar[] >( () =>
  filteredWebinars.value
    .filter( ( w ) => isUpcoming( w ) )
    .sort( ( a, b ) =>
      normalizeDateTime( a.webinarDate ).toMillis() -
      normalizeDateTime( b.webinarDate ).toMillis()
    )
);

const viewWebinars = computed< Webinar[] >( () =>
  view.value === 'past' ? pastWebinars.value : upcomingWebinars.value
);

const { filteredContentPage, resetPageNumber } = usePagingFilter();
const viewWebinarsPage = computed< Webinar[] >( () =>
  filteredContentPage< Webinar >( viewWebinars.value )
);

const hasAnyResultsInView = computed< boolean >( () => viewWebinars.value.length > 0 );

const pastCount = computed< number >( () => pastWebinars.value.length );
const upcomingCount = computed< number >( () => upcomingWebinars.value.length );

const viewResultsLabel = computed< string >( () => {
  const count = viewWebinars.value.length;
  const noun = `webinar${ count === 1 ? '' : 's' }`;
  const qualifier = view.value === 'past' ? 'past' : 'upcoming';
  return `${ count } ${ qualifier } ${ noun }`;
} );

function setView( next: WebinarView ): void {
  if ( view.value === next ) return;
  view.value = next;
  resetPageNumber();
}

provide( searchContextSymbol, {
  breadcrumbItems,
  contentBySlug: webinarBySlug,
  contentCategories: webinarCategories,
  contentTitle: "Webinars",
  contentType: webinarKey,
  filteredContent: viewWebinars,
  filteredContentPage: viewWebinarsPage,
  hasAnyResults: hasAnyResultsInView,
  isLoading: isLoading,
  hideCategoryFilter: false,
  hideProductFilter: false,
  searchPlaceholder: "Search Webinars",
  searchResultsLabel: viewResultsLabel,
  setSelectedContent: setSelectedWebinar
} );

const productFilter = inject< ProductFilter >( productFilterSymbol ) as ProductFilter;
const categoryFilter = inject< CategoryFilter >( categoryFilterSymbol ) as CategoryFilter;
const pagingFilter = inject< PagingFilter >( pagingFilterSymbol ) as PagingFilter;
const { clearDateRangeFilter, datesFilter } = useDateRangeFilter();

const show = ref< boolean >( false );
onMounted( async () => {
  // Clear all product filters set
  productFilter?.resetSelectedProducts();

  // Clear all category filters set
  categoryFilter?.resetSelectedCategories();

  // Reset the page # of the results back to 0
  pagingFilter?.resetPageNumber();

  const now = new Date();
  const oneMonthAhead = new Date( now );
  oneMonthAhead.setMonth( oneMonthAhead.getMonth() + 1 );
  datesFilter.value = [ datesFilter.value[ 0 ], oneMonthAhead ];

  webinarsStore.initialize();

  show.value = true;
} );

onUnmounted( () => {
  show.value = false;
  clearDateRangeFilter();
} );

webinarsStore.initialize();
</script>

<template>
  <transition mode="out-in" name="slide-down">
    <ContentSearch v-if="show">
      <template #sidebar-header>
        <div
          class="grid grid-cols-2 gap-0.5 rounded-md border border-base-300/80 bg-base-100 p-0.5 text-xs font-semibold shadow-sm dark:border-base-content/15 dark:bg-base-200/80"
          role="tablist"
          aria-label="Webinar view"
        >
          <button
            type="button"
            role="tab"
            class="rounded px-2 py-1.5 transition-colors"
            :class="view === 'past'
              ? 'bg-primary/15 text-primary dark:bg-primary/25'
              : 'text-base-content/70 hover:bg-base-200/70 hover:text-base-content'"
            :aria-selected="view === 'past'"
            @click="setView( 'past' )"
          >
            Past
            <span
              class="ms-1 tabular-nums text-[0.65rem] font-normal"
              :class="view === 'past' ? 'text-primary/80' : 'text-base-content/55'"
            >{{ pastCount }}</span>
          </button>
          <button
            type="button"
            role="tab"
            class="rounded px-2 py-1.5 transition-colors"
            :class="view === 'upcoming'
              ? 'bg-primary/15 text-primary dark:bg-primary/25'
              : 'text-base-content/70 hover:bg-base-200/70 hover:text-base-content'"
            :aria-selected="view === 'upcoming'"
            @click="setView( 'upcoming' )"
          >
            Upcoming
            <span
              class="ms-1 tabular-nums text-[0.65rem] font-normal"
              :class="view === 'upcoming' ? 'text-primary/80' : 'text-base-content/55'"
            >{{ upcomingCount }}</span>
          </button>
        </div>
      </template>
    </ContentSearch>
  </transition>
</template>
